<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { useBillingUtils } from '@/use/billingUtils';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { CurrentSubscriptionInfo, Offerings, PlanProduct } from '@/types/billing';
import { Button, LayoutGap, Loader, Prose } from '@dabble/quillui';
import CheckoutFrame from '@/components/CheckoutFrame/CheckoutFrame.vue';
import PlanCard from '@/components/PlanCard/PlanCard.vue';
import BillingCurrencySwitcher from '@/components/BillingCurrencySwitcher/BillingCurrencySwitcher.vue';

const billingStore = useBillingStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { formatUnixDate } = useBillingUtils();

type PlanInterval = 'month' | 'year' | 'lifetime';

const selection = ref<PlanInterval>('month');

// A lifetime holder only gets here with an upgrade on offer, and a lifetime is all they can buy:
// dabble-rest refuses them any recurring plan (LIFETIME_OWNED). So no tabs, no currency to pick.
const canUpgradeLifetime = computed(() => billingStore.canUpgradeLifetime);
const shownInterval = computed<PlanInterval>(() => (canUpgradeLifetime.value ? 'lifetime' : selection.value));

// A link can open the picker on a chosen tab: `/billing/plans?interval=lifetime`
// is what the marketing site's lifetime buttons point at. An unknown value is
// ignored, so a mistyped link still lands on the default tab.
function linkedInterval(): PlanInterval | null {
  const raw = route.query.interval;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value === 'month' || value === 'year' || value === 'lifetime' ? value : null;
}

// The link's tab wins; then a gift card's, which only Lifetime takes; otherwise a yearly
// subscriber starts on Annually.
function applyInitialSelection() {
  const linked = linkedInterval();
  if (linked) selection.value = linked;
  else if (billingStore.checkoutGift) selection.value = 'lifetime';
  else if (currentPlanDetails.value?.interval === 'year') selection.value = 'year';
}

// A paused subscription can't change plan: every plan change is invoiced immediately
// (dabble-rest forces proration_behavior 'always_invoice' so a declined card fails the
// change instead of leaving a past-due invoice), and Stripe refuses to create an invoice
// for a paused subscription. Letting the change through with any other proration mode
// would have the proration voided by pause_collection — a free upgrade — so the rule is
// resume first, then change. The portal page already hides Change Plan while paused; this
// route is reached directly from the app's Upgrade CTA, so it needs its own gate. DAB-1327.
const isPaused = computed(() => billingStore.isPausedSubscription);
const isPauseLocked = computed(() => billingStore.isPauseLocked);
const pauseLockedUntilFormatted = computed(() => formatUnixDate(billingStore.pauseLockedUntil));

function goToPortal() {
  router.push({ name: 'portal' });
}

// Signed support "old pricing" link handling. `offerLinkError` drives a friendly
// panel so a dead link never looks broken; `linkResolving` keeps the loader up
// while we swap the current catalog for the older offering.
type OfferLinkError = 'expired' | 'invalid' | 'wrong-account';
const offerLinkError = ref<OfferLinkError | null>(null);
const linkResolving = ref(false);

const offerErrorTitle = computed(() => {
  if (offerLinkError.value === 'expired') return t('checkout_offer_expired_title');
  if (offerLinkError.value === 'wrong-account') return t('checkout_offer_wrong_account_title');
  return t('checkout_offer_invalid_title');
});
const offerErrorBody = computed(() => {
  if (offerLinkError.value === 'expired') return t('checkout_offer_expired_body');
  if (offerLinkError.value === 'wrong-account') return t('checkout_offer_wrong_account_body');
  return t('checkout_offer_invalid_body');
});

async function viewCurrentPlans() {
  sessionStorage.removeItem('offer');
  billingStore.clearOfferToken();
  offerLinkError.value = null;
  await billingStore.fetchBillingOverview();
}

const intervals = [
  { value: 'month', label: 'checkout_interval_monthly' },
  { value: 'year', label: 'checkout_interval_annually' },
  { value: 'lifetime', label: 'checkout_interval_lifetime' },
] as const;

const offerings = computed(() => billingStore.offerings as Offerings | null);
const hasPlans = computed(() => !!offerings.value?.products);
const isChangingPlan = computed(() => billingStore.isActivePaid);

const currentPlanDetails = computed<CurrentSubscriptionInfo | null>(() => {
  const sub = billingStore.primarySubscription;
  if (!sub) return null;

  const productId = sub.plan?.product?.id;

  return {
    id: sub.plan?.id ?? '',
    product: productId ? { id: productId, name: sub.plan?.product?.name ?? '' } : undefined,
    interval: sub.plan?.interval ?? 'month',
    status: sub.status,
    metadata: sub.metadata,
    offerings: billingStore.offerings ?? undefined,
  };
});

const isTrialing = computed(() => billingStore.isTrialing);

onMounted(async () => {
  const token = sessionStorage.getItem('offer');
  if (token) {
    linkResolving.value = true;
    // Resolve the offer first so the grid is only ever the older offering (never a
    // flash of current pricing). On success, refresh billing overview for the
    // subscription/customer state checkout needs; the offer grid is preserved.
    const res = await billingStore.fetchOfferingForToken(token);
    if (res.ok) {
      await billingStore.fetchBillingOverview();
    } else if (res.reason !== 'network') {
      // A dead link (expired / invalid / wrong-account) gets a friendly panel.
      // A transient network failure is left to a reload, keeping the token.
      offerLinkError.value = res.reason;
      billingStore.clearOfferToken();
      if (res.reason !== 'wrong-account') sessionStorage.removeItem('offer');
    }
    applyInitialSelection();
    linkResolving.value = false;
    return;
  }

  applyInitialSelection();
  if (!offerings.value && !billingStore.isLoading) {
    billingStore.fetchBillingOverview();
  }
});

// Representative recurring amount keeps card order stable across all toggle positions.
function sortAmount(product: PlanProduct | undefined): number | null {
  return product?.month?.amount ?? product?.year?.amount ?? product?.['one-time']?.amount ?? null;
}

const orderedProductIDs = computed(() => {
  const plansData = offerings.value?.products;
  if (!plansData) return [];

  return Object.keys(plansData).sort((aKey, bKey) => {
    const priceA = sortAmount(plansData[aKey]);
    const priceB = sortAmount(plansData[bKey]);

    if (priceA === priceB) return 0;
    if (priceA === null) return 1;
    if (priceB === null) return -1;

    return priceA < priceB ? -1 : 1;
  });
});

// Recommend the upgrade on offer, else the middle plan (the second tier when there are three).
const recommendedProductID = computed(() => {
  const upgradeId = canUpgradeLifetime.value && billingStore.lifetimeUpgrade?.product?.id;
  if (upgradeId) return upgradeId;
  const ids = orderedProductIDs.value;
  return ids.length ? ids[Math.floor(ids.length / 2)] : null;
});
</script>

<template>
  <CheckoutFrame :back-to="{ name: 'portal' }" :back-label="t('checkout_back_to_billing')">
    <!-- Hidden while paused: "Choose your plan" over a panel saying no plan can be chosen
         contradicts itself. The paused notice carries its own heading. -->
    <header v-if="!isPaused" class="plans__header">
      <h1 class="plans__title">
        {{ isChangingPlan ? t('checkout_plans_title_change') : t('checkout_plans_title_choose') }}
      </h1>
      <Prose variant="secondary" align="center" class="plans__subtitle">
        {{ t('checkout_plans_subtitle') }}
      </Prose>
    </header>

    <!-- Paused outranks everything below, including a support offer link: no plan can be
         chosen until the subscription is resumed, so there is nothing to show but the way
         out. The lock line reuses the portal's own string, always visible — never a hover. -->
    <div v-if="isPaused" class="plans__notice" data-testid="plans-paused">
      <h2 class="plans__notice-title">{{ t('billing_paused') }}</h2>
      <Prose align="center" variant="secondary">{{ t('billing_plans_paused_body') }}</Prose>
      <Prose v-if="isPauseLocked" align="center" variant="secondary">
        {{ t('billing_pause_lock_tooltip', { date: pauseLockedUntilFormatted }) }}
      </Prose>
      <Button variant="primary" @click="goToPortal">{{ t('checkout_back_to_billing') }}</Button>
    </div>

    <div v-else-if="offerLinkError" class="plans__notice">
      <h2 class="plans__notice-title">{{ offerErrorTitle }}</h2>
      <Prose align="center" variant="secondary">{{ offerErrorBody }}</Prose>
      <Button variant="primary" @click="viewCurrentPlans">{{ t('checkout_offer_view_current') }}</Button>
    </div>

    <template v-else>
      <div v-if="hasPlans && !canUpgradeLifetime" class="plans__currency">
        <BillingCurrencySwitcher />
      </div>

      <div
        v-if="hasPlans && !canUpgradeLifetime"
        class="plans__interval"
        role="group"
        :aria-label="t('checkout_toggle_interval_aria')"
      >
        <div class="plans__interval-pills" :class="`plans__interval-pills--${selection}`">
          <span class="plans__interval-highlight" aria-hidden="true" />
          <button
            v-for="option in intervals"
            :key="option.value"
            type="button"
            class="plans__interval-pill"
            :class="{ 'plans__interval-pill--active': selection === option.value }"
            :aria-pressed="selection === option.value"
            @click="selection = option.value"
          >
            {{ t(option.label) }}
          </button>
        </div>
        <Prose size="small" variant="secondary" class="plans__interval-note">{{ t('checkout_pay_annually') }}</Prose>
      </div>

      <div v-if="hasPlans" class="plans__grid">
        <PlanCard
          v-for="prodID in orderedProductIDs"
          :key="prodID"
          :product="offerings!.products[prodID]"
          :product-id="prodID"
          :current="currentPlanDetails"
          :is-trialing="isTrialing"
          :selection="shownInterval"
          :offerings="offerings"
          :recommend="prodID === recommendedProductID"
        />
      </div>

      <LayoutGap
        v-else-if="billingStore.isLoading || linkResolving"
        direction="column"
        align="center"
        class="plans__status"
      >
        <Loader />
      </LayoutGap>

      <Prose v-else align="center" variant="secondary" class="plans__status">
        {{ t('billing_plans_load_error') }}
      </Prose>
    </template>
  </CheckoutFrame>
</template>

<style lang="scss" scoped>
.plans__currency {
  display: flex;
  justify-content: center;
  margin-top: space('small');
}

.plans__header {
  text-align: center;
  max-width: 34rem;
}

.plans__title {
  margin: 0;
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-size: fontSize('xxlarge');
  font-weight: fontWeight('bold');
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: theme('color-text-primary');
}

.plans__subtitle {
  margin-top: space('xsmall');
}

.plans__interval {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: space('xsmall');
  margin-top: space('large');
}

.plans__interval-pills {
  position: relative;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  width: 22.5rem;
  max-width: 100%;
  padding: space('tiny');
  background-color: theme('color-bg-app-overlay');
  border-radius: 9999px;
}

.plans__interval-highlight {
  position: absolute;
  top: space('tiny');
  bottom: space('tiny');
  left: space('tiny');
  width: calc((100% - #{space('tiny')} * 2) / 3);
  background-color: theme('color-fg-app');
  border-radius: 9999px;
  box-shadow: elevation('small');
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.plans__interval-pills--year .plans__interval-highlight {
  transform: translateX(100%);
}

.plans__interval-pills--lifetime .plans__interval-highlight {
  transform: translateX(200%);
}

.plans__interval-pill {
  position: relative;
  z-index: 1;
  padding: space('xxsmall') 0;
  background: none;
  border: none;
  border-radius: 9999px;
  color: theme('color-text-secondary');
  font: inherit;
  font-size: fontSize('small');
  font-weight: fontWeight('semibold');
  cursor: pointer;
  transition: color 180ms ease;

  &--active {
    color: theme('color-text-primary');
  }

  &:focus-visible {
    outline: 2px solid theme('color-fg-primary-interactive');
    outline-offset: 2px;
  }
}

.plans__interval-note {
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.plans__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: space('medium');
  width: 100%;
  margin-top: space('xlarge');
}

.plans__status {
  margin-top: space('xxlarge');
}

.plans__notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: space('medium');
  max-width: 32rem;
  margin: space('xxlarge') auto 0;
  text-align: center;
}

.plans__notice-title {
  margin: 0;
  font-family: var(--font-family-ui-serif, ui-serif, Georgia, serif);
  font-size: fontSize('xlarge');
  font-weight: fontWeight('bold');
  color: theme('color-text-primary');
}

@media (max-width: 860px) {
  .plans__grid {
    grid-template-columns: minmax(0, 26rem);
    justify-content: center;
  }
}
</style>
